import {UsageEvent} from '../types';
import {
    PlatformPropertyCard,
    PlatformPropertyCardCopyButtonDecorator,
    PlatformPropertyCardEntryInput
} from "@flexinet/ui-components";
import {Code, ScrollArea, Stack, Text, Title} from "@mantine/core";
import {I18n} from '@aws-amplify/core';
import {BillableBadge} from "./Badges.tsx";
import {prettyPrintAttributes} from "../utils/parseAttributes.ts";
import {MonoText} from "../components/MonoText";

const PLACEHOLDER = '—';

// Typed as PlatformPropertyCardEntryInput[], never cast: the old
// `as PlatformPropertyCardProperty[]` hid a misspelt formatter key the card does
// not read, so both timestamps rendered as raw ISO strings (FNP-1883; same class as
// FNP-1322). `formatter: 'timestamp'` is the library's key.
export const Profile = ({record}: { record: UsageEvent }) => {

    const identityProperties: PlatformPropertyCardEntryInput[] = [
        {
            label: I18n.get('ID'),
            value: record.id,
            decorator: (value: string) => <PlatformPropertyCardCopyButtonDecorator value={value}/>,
            span: 12,
        },
        {
            label: I18n.get('Event ID'),
            value: record.eventId,
            decorator: (value: string) => <PlatformPropertyCardCopyButtonDecorator value={value}/>,
            span: 12,
        },
        {
            label: I18n.get('Status'),
            value: <BillableBadge record={record}/>,
            span: 6,
        },
        {
            label: I18n.get('Meter Code'),
            value: record.meterCode ?? PLACEHOLDER,
            span: 6,
        },
    ];

    const attributionProperties: PlatformPropertyCardEntryInput[] = [
        {
            label: I18n.get('Account ID'),
            value: <MonoText size='sm' value={record.accountId}/>,
            span: 6,
        },
        {
            label: I18n.get('Customer ID'),
            value: <MonoText size='sm' value={record.customerId}/>,
            span: 6,
        },
        {
            label: I18n.get('Quantity'),
            value: typeof record.quantity === 'number' ? String(record.quantity) : PLACEHOLDER,
            span: 6,
        },
        {
            label: I18n.get('Customer Meter Key'),
            value: <MonoText size='sm' value={record.customerMeterKey}/>,
            span: 6,
        },
        {
            label: I18n.get('Occurred At'),
            value: record.occurredAt,
            formatter: 'timestamp',
            span: 6,
        },
        {
            label: I18n.get('Ingested At'),
            value: record.receivedAt,
            formatter: 'timestamp',
            span: 6,
        },
    ];

    const attributes = prettyPrintAttributes(record.attributes);

    return (
        <Stack gap={'lg'}>
            <PlatformPropertyCard properties={identityProperties}/>
            <PlatformPropertyCard properties={attributionProperties}/>

            <Stack gap={'xs'}>
                <Title order={5}>{I18n.get('Attributes')}</Title>
                {attributes
                    ? <ScrollArea.Autosize mah={400}>
                        <Code block>{attributes}</Code>
                    </ScrollArea.Autosize>
                    : <Text c={'dimmed'} size={'sm'}>{I18n.get('No attributes on this event.')}</Text>}
            </Stack>
        </Stack>
    );
};
